import { AdminSectionCard } from '../primitives/admin-section-card.js';
import { AdminEmptyState } from '../primitives/admin-empty-state.js';
import { AdminErrorState } from '../primitives/admin-error-state.js';
import { AdminSkeleton } from '../primitives/admin-skeleton.js';
import { AdminOrderStatusBadge } from '../primitives/admin-status-badge.js';
import { formatDateTime, formatPaise } from '../../lib/format.js';
import type { RecentOrderDto } from '../../types/dashboard.js';

export interface RecentOrdersListProps {
  orders: RecentOrderDto[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

/**
 * Bounded (server already caps this at 10 rows), read-only preview -- not
 * the full order-management table (Milestone 3). Each row is intentionally
 * non-clickable this milestone (Part 9): a detail route doesn't exist yet,
 * so there is nothing safe to link to.
 */
export function RecentOrdersList({ orders, isLoading, isError, onRetry }: RecentOrdersListProps) {
  return (
    <AdminSectionCard title="Recent orders" description="The 10 most recently placed orders">
      {isLoading && (
        <div className="space-y-3">
          <AdminSkeleton lines={4} className="h-10" />
        </div>
      )}
      {!isLoading && isError && <AdminErrorState message="Recent orders could not be loaded." onRetry={onRetry} />}
      {!isLoading && !isError && orders.length === 0 && (
        <AdminEmptyState title="No orders yet" description="Orders will appear here once customers start placing them." />
      )}
      {!isLoading && !isError && orders.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-400">
                <th scope="col" className="py-2 pr-4 font-medium">Order</th>
                <th scope="col" className="py-2 pr-4 font-medium">Customer</th>
                <th scope="col" className="py-2 pr-4 font-medium">Total</th>
                <th scope="col" className="py-2 pr-4 font-medium">Status</th>
                <th scope="col" className="py-2 pr-4 font-medium">Payment</th>
                <th scope="col" className="py-2 pr-4 font-medium">Placed</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id} className="border-b border-gray-50 last:border-0">
                  <td className="py-2.5 pr-4 font-medium text-gray-900">{order.orderNumber}</td>
                  <td className="py-2.5 pr-4 text-gray-600">{order.customerDisplayName}</td>
                  <td className="py-2.5 pr-4 text-gray-900">{formatPaise(order.totalPaise)}</td>
                  <td className="py-2.5 pr-4"><AdminOrderStatusBadge status={order.status} /></td>
                  <td className="py-2.5 pr-4 text-gray-600 capitalize">{order.paymentMethod}</td>
                  <td className="py-2.5 pr-4 whitespace-nowrap text-gray-500">{formatDateTime(order.placedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminSectionCard>
  );
}
